import { FormikErrors, useFormik } from 'formik'
import React, { ChangeEvent, useState } from 'react'
import ModalComponent from '..'
import { Button } from '../../button'
import { TextInput } from '../../inputs/textInput'

import { AddProductVariantSchema } from '../../../validation/validate'

import { NumericFormat } from 'react-number-format'
import {
  getCurrencySymbol,
  parseCurrencyToNumber,
  removeElementAtIndex,
} from '../../../helpers'
import { useModalControl } from '../../../hooks/useModalControl'
import {
  CreatedProductVariantOption,
  SingleVariant,
} from '../../../redux-toolkits/products/products.type'
import { ImageUploadButton } from '../../button/imageUploadButton'
import Icon from '../../icon'
import { ImageBanner } from '../../images/imageBanner'
import { Checkbox } from '../../inputs/Checkbox'
import Dropdown from '../../inputs/dropdown'
import { ImageUploadPreviewModal } from './ImagePreviewModal'

export const AddProductVariantModal: React.FC<{
  open: boolean
  setOpen: (cal: boolean) => void
  onCloseCallback?: () => void
  onSuccessCallback?: (val: CreatedProductVariantOption[]) => void
  mode: 'edit' | 'create'
  selectedVariant?: SingleVariant
  productVariants?: CreatedProductVariantOption[]
  loading?: boolean
  variants: SingleVariant[]
}> = ({
  open,
  setOpen,
  onCloseCallback = () => {},
  onSuccessCallback = (val) => {},
  mode,
  selectedVariant,
  productVariants,
  loading,
  variants,
}) => {
  const [uploadedImage, setUploadedImage] = useState<File>()
  const [uploadValueIndex, setUploadValueIndex] = useState<number>()

  const { handleModalOpen, modal, modalOpen, setModalOpen } = useModalControl()

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) {
      return
    }

    const selectedFile = event.target.files[0]
    setUploadedImage(selectedFile)

    handleModalOpen('imageUploadPreviewModal')
  }

  const {
    values,
    errors,
    handleSubmit,
    handleChange,
    handleBlur,
    touched,
    isValid,
    resetForm,
    setFieldValue,
  } = useFormik({
    initialValues: {
      variant: selectedVariant,
      values:
        productVariants?.filter(
          (item) => item.variant?._id === selectedVariant?._id,
        ) ?? [],
    },
    enableReinitialize: true,
    validationSchema: AddProductVariantSchema,
    onSubmit: (vals, { resetForm }) => {
      const itemsToAdd = vals.values.map((val) => {
        return {
          ...val,
          price: parseCurrencyToNumber(val.price),
          variant: values.variant as SingleVariant,
        }
      })

      onSuccessCallback(itemsToAdd)
      resetForm()
    },
  })
  return (
    <ModalComponent
      open={open}
      setOpen={setOpen}
      onCloseCallback={() => {
        resetForm()
        onCloseCallback()
      }}
      title={mode === 'create' ? 'Add Option' : 'Edit Option'}
      childrenClassName="md:!max-w-[420px]"
    >
      <div className="flex flex-col gap-4">
        <Dropdown
          loading={loading}
          className="w-full"
          menuClassName="!max-h-[200px]"
          menuButton={
            <TextInput
              type="text"
              name="variant"
              placeholder="Choose variant"
              value={values.variant?.name ?? ''}
              onBlur={handleBlur}
              onChange={handleChange}
              errors={errors.variant && touched.variant ? errors.variant : ''}
              hasIcon={false}
              readOnly
              extra={
                <Icon
                  id={'left-caret'}
                  width={16}
                  height={16}
                  className="-rotate-90 text-sec-black"
                />
              }
              className="!border-divider-gray !w-full"
              title="Variant"
            />
          }
          menuItems={variants.map((variant) => {
            return {
              name: variant.name,
              value: variant,
              onClick: () => {
                setFieldValue('variant', variant)
              },
            }
          })}
        />

        <div className="flex flex-col gap-4">
          {values.values.map((value, index) => {
            const key = `${index}`

            return (
              <div
                key={key}
                className="flex flex-col gap-4 border p-2 rounded-lg"
              >
                <div className="flex items-center justify-between gap-4">
                  <p className="text-black font-medium text-lg font-recoleta">
                    Option {index + 1} - ({values.values[index]?.value ?? ''})
                  </p>

                  <button
                    type="button"
                    className="w-[24px] !h-[24px] flex items-center justify-center bg-danger rounded-full"
                    onClick={() => {
                      const valuesCopy = JSON.parse(
                        JSON.stringify(values.values),
                      )

                      setFieldValue(
                        'values',
                        removeElementAtIndex(valuesCopy, index),
                      )
                    }}
                  >
                    <Icon
                      className="text-white"
                      width={14}
                      height={14}
                      id="bin"
                    />
                  </button>
                </div>

                <div className="flex-1 grid grid-cols-2 gap-4">
                  <TextInput
                    type="text"
                    name={`values[${index}].value`}
                    placeholder="Value"
                    value={String(value?.value ?? '')}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    errors={
                      errors?.values && touched?.values
                        ? (
                            errors?.values as FormikErrors<CreatedProductVariantOption>[]
                          )[index]?.value
                        : ''
                    }
                    hasIcon={false}
                    className=""
                    title="Value"
                  />

                  <NumericFormat
                    suffix={undefined}
                    prefix={getCurrencySymbol('NGN')}
                    decimalScale={2}
                    thousandSeparator
                    customInput={TextInput}
                    type="text"
                    name={`values[${index}].price`}
                    placeholder="Price"
                    value={value.price}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    errors={
                      errors?.values && touched?.values
                        ? (
                            errors?.values as FormikErrors<CreatedProductVariantOption>[]
                          )[index]?.price
                        : ''
                    }
                    hasIcon={false}
                    className=""
                    title="Price"
                  />

                  <div className="col-span-full flex flex-col gap-4 justify-center">
                    <Checkbox
                      checkStatus={
                        !['0', 0, undefined].includes(value.markupValue)
                      }
                      text="Enable markup percentage"
                      handleChange={(checked) => {
                        if (checked) {
                          setFieldValue(`values[${index}].markupValue`, null)
                        } else {
                          setFieldValue(
                            `values[${index}].markupValue`,
                            undefined,
                          )
                        }
                      }}
                    />

                    {!['0', 0, undefined].includes(value.markupValue) && (
                      <TextInput
                        type="text"
                        title="Markup percentage"
                        name={`values[${index}].markupValue`}
                        placeholder="Markup value"
                        value={
                          value.markupValue ? String(value.markupValue) : ''
                        }
                        onBlur={handleBlur}
                        onChange={handleChange}
                        hasIcon={false}
                        className=""
                        errors={
                          errors?.values && touched?.values
                            ? (
                                errors?.values as FormikErrors<CreatedProductVariantOption>[]
                              )[index]?.markupValue
                            : ''
                        }
                      />
                    )}
                  </div>

                  {(value.images ?? []).length > 0 && (
                    <div className="relative w-full col-span-full grid grid-cols-2 gap-2">
                      {value.images?.map((url, idx) => {
                        const key = `${idx}`

                        return (
                          <ImageBanner
                            imageUrl={url}
                            loading={false}
                            className="
                            !w-full !max-h-[150px]
                            "
                            errors={''}
                            key={key}
                            onDelete={() => {
                              const imagesCopy = JSON.parse(
                                JSON.stringify(value.images),
                              )

                              setFieldValue(
                                `values[${index}].images`,
                                removeElementAtIndex(imagesCopy, idx),
                              )
                            }}
                          />
                        )
                      })}
                    </div>
                  )}

                  <ImageUploadButton
                    handleChange={(e) => {
                      setUploadValueIndex(index)
                      handleFileChange(e)
                    }}
                    showText
                    icon={'pic-icon'}
                    iconDim={52}
                    text={`Upload ${values.values[index]?.value ?? ''} Images`}
                    className="
                    !relative !w-full
                    !m-0 !top-0 !right-0
                    !h-fit
                    !rounded-lg
                    !col-span-full
                    "
                  />
                </div>
              </div>
            )
          })}

          <button
            type="button"
            className="flex items-center gap-2 mt-2 font-medium"
            onClick={() => {
              setFieldValue('values', [...values.values, {}])
            }}
          >
            Add Option
            <Icon id="add-icon-2" width={20} height={20} />
          </button>
        </div>

        <Button
          label="Proceed"
          type="button"
          className="mt-10 sticky bottom-0"
          disabled={!isValid}
          loading={false}
          primary
          onClick={() => {
            handleSubmit()
          }}
        />

        {uploadedImage && uploadValueIndex !== undefined && (
          <ImageUploadPreviewModal
            image={uploadedImage}
            open={modal === 'imageUploadPreviewModal' && modalOpen}
            setOpen={setModalOpen}
            onCloseCallback={() => {
              setUploadedImage(undefined)
              setUploadValueIndex(undefined)
            }}
            onSuccessCallback={(url) => {
              setFieldValue(`values[${uploadValueIndex}].images`, [
                ...(values.values[uploadValueIndex].images ?? []),
                url,
              ])
            }}
          />
        )}
      </div>
    </ModalComponent>
  )
}
